// Reuse the exact same input styles used by ThoughtInput (was importing the component, not the CSS)
import { useEffect, useMemo, useRef, useState } from "react";

import AddToArtlistIcon from "@/assets/all-icons/add-to-artlist-icon";
import StickersIcon from "@/assets/all-icons/stickers-icon";
import UploadArrowUp from "@/shared-components/icons/components/actions/UploadArrowUp";
import XIcon from "@/shared-components/icons/components/public/XIcon";
import StickerMenu from "@/shared-components/stickers/StickerMenu";

import "@/pages/artwork/content-section/socials/artwork-thoughts/components/styles/ThoughtInput.css";
import "./styles/ConversationInput.css";

export default function ConversationInput({
  sendMessage,
  stickers = [],
  stickersLoading = false,
  galleryId,
  className = "",
  allowMediaUploads = false,
  onMediaSelect = null,
  mediaUploading = false,
  replyToMessage = null,
  onClearReply = null,
}) {
  const textareaRef = useRef();
  const fileInputRef = useRef(null);
  const [isStickerPickerOpen, setIsStickerPickerOpen] = useState(false);
  const [isTextareaExpanded, setIsTextareaExpanded] = useState(false);
  const availableStickers = useMemo(
    () => (Array.isArray(stickers) ? stickers : []),
    [stickers],
  );

  const canOpenStickerPicker = Boolean(galleryId || stickersLoading || availableStickers.length > 0);

  function resizeTextarea() {
    if (!textareaRef.current) return;

    textareaRef.current.setAttribute("rows", "1");
    const textareaStyles = window.getComputedStyle(textareaRef.current);
    const lineHeight = parseFloat(textareaStyles.lineHeight) || 20;
    const verticalPadding =
      (parseFloat(textareaStyles.paddingTop) || 0) + (parseFloat(textareaStyles.paddingBottom) || 0);
    const contentHeight = Math.max(0, textareaRef.current.scrollHeight - verticalPadding);
    const numRows = Math.max(1, Math.ceil(contentHeight / lineHeight));
    textareaRef.current.setAttribute("rows", String(numRows));
    setIsTextareaExpanded(numRows > 1);
  }

  function onSendMessage() {
    if (mediaUploading) return;
    const message = textareaRef.current.value.trim();
    if (message.length === 0) return;
    sendMessage({ text: message });
    textareaRef.current.value = "";
    resizeTextarea();
    if (typeof onClearReply === "function") {
      onClearReply();
    }
  }

  function onStickerSelect(sticker) {
    if (!sticker) return;
    sendMessage({ sticker });
    setIsStickerPickerOpen(false);
    if (typeof onClearReply === "function") {
      onClearReply();
    }
  }

  useEffect(() => {
    if (!canOpenStickerPicker && isStickerPickerOpen) {
      setIsStickerPickerOpen(false);
    }
  }, [canOpenStickerPicker, isStickerPickerOpen]);

  useEffect(() => {
    if (replyToMessage && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [replyToMessage]);

  const handleMediaButtonClick = () => {
    if (!allowMediaUploads || mediaUploading) return;
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event) => {
    const files = event?.target?.files ? Array.from(event.target.files) : [];
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (!allowMediaUploads || typeof onMediaSelect !== "function" || files.length === 0) {
      return;
    }
    onMediaSelect(files);
  };

  return (
    <>
      {replyToMessage ? (
        <div className="conversation-input__reply-status">
          <p className="conversation-input__reply-text">
            Replying to <span>{replyToMessage.senderUsername || "Unknown user"}</span>
          </p>
          <button
            type="button"
            className="conversation-input__reply-close"
            onClick={onClearReply}
            aria-label="Cancel reply"
            title="Cancel reply"
          >
            <XIcon />
          </button>
        </div>
      ) : null}
      <form
        onSubmit={event => { event.preventDefault(); onSendMessage(); }}
        className={`${className} artwork-view-thoughts__input-container conversation`.trim()}
      >
        <div className="artwork-view-thoughts__textarea-outter-container">
          <div className={`artwork-view-thoughts__textarea-container ${isTextareaExpanded ? "is-expanded" : ""}`}>
            {allowMediaUploads ? (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/*"
                  className="conversation-input__file-input"
                  onChange={handleFileChange}
                />
                <button
                  type="button"
                  className="artwork-view-thoughts__media-button"
                  onClick={handleMediaButtonClick}
                  disabled={mediaUploading}
                  aria-label="Attach file"
                >
                  {mediaUploading ? (
                    <span className="artwork-view-thoughts__media-spinner" />
                  ) : (
                    <AddToArtlistIcon className="profile-add-button__icon conversation-input__media-icon" />
                  )}
                </button>
              </>
            ) : null}

            <textarea
              placeholder="Start chatting"
              className="artwork-view-thoughts__textarea remove-scrollbar"
              rows="1"
              onInput={resizeTextarea}
              ref={textareaRef}
              spellCheck={false}
            />

            <div className="artwork-view-thoughts__actions">
              <button
                type="button"
                className="artwork-view-thoughts__sticker-button"
                onClick={() => canOpenStickerPicker && setIsStickerPickerOpen(true)}
                aria-label="Open sticker picker"
                disabled={!canOpenStickerPicker}
              >
                <StickersIcon className="conversation-input__sticker-icon" />
              </button>
              <button
                type="submit"
                className="artwork-view-thoughts__publish-thought"
                disabled={mediaUploading}
              >
                <div className="artwork-view-thoughts__publish-thought-circle">
                  <UploadArrowUp />
                </div>
              </button>
            </div>
          </div>
        </div>
      </form>

      {canOpenStickerPicker && (
        <StickerMenu
          open={isStickerPickerOpen}
          close={() => setIsStickerPickerOpen(false)}
          stickers={availableStickers}
          isLoading={stickersLoading}
          onSelectSticker={onStickerSelect}
        />
      )}
    </>
  );
}
